import { Link } from "react-router-dom";
import { BookOpen, Trophy, MessageCircle, Zap, CheckCircle, Star, Target, Lightbulb, TrendingUp, GraduationCap, Shield, Flag, Users } from "lucide-react";

const Landing = () => {
  const features = [
    { icon: Star, title: "STAAR by Grade", desc: "Grades 3-12 with grade-specific practice" },
    { icon: BookOpen, title: "SAT & ACT", desc: "College admission test preparation" },
    { icon: MessageCircle, title: "AI Tutor", desc: "Get instant help when you're stuck" },
    { icon: Trophy, title: "Track Progress", desc: "Know when you're ready with Exam Readiness scores" },
  ];

  // Student tests prominently featured
  const studentTests = [
    { name: "STAAR", grades: "3-12", color: "bg-sky-500", desc: "Texas State Tests" },
    { name: "SAT", grades: "11-12", color: "bg-indigo-500", desc: "College Admission" },
    { name: "ACT", grades: "11-12", color: "bg-lime-500", desc: "College Testing" },
    { name: "ASVAB", grades: "11+", color: "bg-orange-500", desc: "Military Careers" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500 to-indigo-600" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white rounded-full" />
          <div className="absolute top-40 right-20 w-24 h-24 bg-lime-400 rounded-full" />
          <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-orange-400 rounded-full" />
        </div>

        <nav className="relative z-10 max-w-6xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-indigo-600 font-black text-2xl">C</span>
            </div>
            <span className="font-heading font-black text-xl sm:text-2xl text-white">ClearPath</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="login-link"
              className="px-4 sm:px-6 py-2.5 text-white font-bold hover:bg-white/10 rounded-full transition-all"
            >
              Log In
            </Link>
            <Link
              to="/register"
              data-testid="register-link"
              className="px-4 sm:px-6 py-2.5 bg-lime-500 text-white font-bold rounded-full shadow-[0_4px_0_0_#65a30d] hover:shadow-[0_2px_0_0_#65a30d] hover:translate-y-[2px] transition-all"
            >
              Start Free
            </Link>
          </div>
        </nav>

        <div className="relative z-10 max-w-6xl mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                <Star className="w-4 h-4 text-yellow-300" />
                <span className="text-white/90 text-sm font-medium">For Texas Students & Families</span>
              </div>
              <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
                Prepare for high-stakes tests<br />
                <span className="text-lime-400">with confidence.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-white/90 max-w-xl">
                Practice, get help when stuck, and know when you're ready.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link
                  to="/register"
                  data-testid="get-started-btn"
                  className="px-8 py-4 bg-lime-500 text-white font-bold text-lg rounded-full shadow-[0_4px_0_0_#65a30d] hover:shadow-[0_2px_0_0_#65a30d] hover:translate-y-[2px] transition-all"
                >
                  Start Free
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-4 bg-white/20 text-white font-bold text-lg rounded-full hover:bg-white/30 transition-all"
                >
                  See Pricing
                </Link>
              </div>
            </div>
            
            {/* Student Test Cards */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                {studentTests.map((test) => (
                  <div 
                    key={test.name}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20"
                  >
                    <div className={`w-12 h-12 ${test.color} rounded-xl mb-3 flex items-center justify-center`}>
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-white">{test.name}</h3>
                    <p className="text-white/70 text-sm">{test.desc}</p>
                    <p className="text-white/50 text-xs mt-1">Grades {test.grades}</p>
                  </div>
                ))}
              </div>
              {/* Adult track mention */}
              <div className="mt-4 text-center">
                <p className="text-white/60 text-sm flex items-center justify-center gap-2">
                  <Flag className="w-4 h-4" />
                  Also: US Citizenship prep for adults
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Value Prop Section - Relief-Focused */}
      <section className="py-12 px-4 bg-white border-b">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="p-6">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Am I Ready?</h3>
              <p className="text-slate-500 text-sm">See your Exam Readiness score so you know—not hope.</p>
            </div>
            <div className="p-6">
              <div className="w-14 h-14 bg-lime-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-lime-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">What Do I Need?</h3>
              <p className="text-slate-500 text-sm">Know exactly which topics need work. No guessing.</p>
            </div>
            <div className="p-6">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Practice Until Ready</h3>
              <p className="text-slate-500 text-sm">Unlimited practice. Clear explanations. Real progress.</p>
            </div>
          </div>
        </div>
      </section>

      {/* STAAR Highlight Section */}
      <section className="py-16 px-4 bg-sky-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-bold mb-4">
              <Star className="w-4 h-4" /> TEXAS STAAR TESTS
            </span>
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-slate-800 mb-4">
              Grade-Specific STAAR Practice
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From 3rd grade math to high school EOC exams—targeted practice for every grade level in Texas.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-5 border-2 border-sky-200 shadow-sm">
              <div className="text-3xl mb-2">📚</div>
              <h3 className="font-bold text-slate-800">Elementary</h3>
              <p className="text-slate-500 text-sm">Grades 3-5</p>
              <p className="text-xs text-sky-600 mt-2">Reading, Math, Science, Writing</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border-2 border-teal-200 shadow-sm">
              <div className="text-3xl mb-2">🎯</div>
              <h3 className="font-bold text-slate-800">Middle School</h3>
              <p className="text-slate-500 text-sm">Grades 6-8</p>
              <p className="text-xs text-teal-600 mt-2">+ Social Studies, Writing</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border-2 border-blue-200 shadow-sm">
              <div className="text-3xl mb-2">🎓</div>
              <h3 className="font-bold text-slate-800">High School EOC</h3>
              <p className="text-slate-500 text-sm">Graduation Required</p>
              <p className="text-xs text-blue-600 mt-2">English, Algebra, Biology, US History</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-center text-slate-800 mb-4">
            How ClearPath Works
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Short sessions, real progress, no wasted time.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "1", icon: Target, title: "Pick your test & grade", desc: "Choose STAAR (by grade), SAT, ACT, or ASVAB. Questions match exactly what's on the exam." },
              { num: "2", icon: Lightbulb, title: "Practice with clear explanations", desc: "Every question comes with a simple breakdown. Get hints when you're stuck." },
              { num: "3", icon: TrendingUp, title: "Track progress to Exam Ready", desc: "Master topics to see your readiness score rise. Know exactly where you stand." },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-16 h-16 bg-indigo-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <step.icon className="w-8 h-8" />
                </div>
                <div className="inline-flex items-center justify-center w-8 h-8 bg-lime-500 text-white font-bold rounded-full mb-3">
                  {step.num}
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">{step.title}</h3>
                <p className="text-slate-500 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-center text-slate-800 mb-12">
            Everything You Need to Succeed
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border-2 border-slate-100 shadow-[0_6px_0_0_#f1f5f9] hover:shadow-[0_8px_0_0_#e0e7ff] hover:border-indigo-200 transition-all"
                >
                  <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-slate-500 text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Adult Track Section - Subtle */}
      <section className="py-12 px-4 bg-red-50 border-y border-red-100">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Flag className="w-6 h-6 text-red-500" />
            <h2 className="font-heading font-bold text-xl text-slate-800">U.S. Citizenship Test Prep</h2>
          </div>
          <p className="text-slate-600 mb-4">
            Designed for parents and adult learners preparing for the naturalization interview.
          </p>
          <p className="text-sm text-slate-500">
            Includes Read Aloud practice and interview simulation.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-indigo-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-white mb-4">
            Prepare with Confidence
          </h2>
          <p className="text-white/90 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            Start free. Unlimited practice when you're ready. Know your score before test day.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-4 bg-lime-500 text-white font-bold text-lg rounded-full shadow-[0_4px_0_0_#65a30d] hover:shadow-[0_2px_0_0_#65a30d] hover:translate-y-[2px] transition-all"
            >
              Start Free
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-white/20 text-white font-bold text-lg rounded-full hover:bg-white/30 transition-all"
            >
              See Pricing
            </Link>
          </div>
          <p className="text-white/60 text-sm mt-6">
            Free includes 3 questions per day. Premium unlocks unlimited practice.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-black text-xl">C</span>
                </div>
                <span className="font-heading font-bold text-xl">ClearPath Testing Solutions</span>
              </div>
              <p className="text-slate-400 text-sm max-w-sm">
                Practice, get help when stuck, and know when you're ready.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About</Link></li>
                <li><Link to="/pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</Link></li>
                <li><Link to="/faq" className="text-slate-400 hover:text-white text-sm transition-colors">FAQ</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Use</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 Clear Path Testing Solutions LLC. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              Made in Texas
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
